import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../store";
import { fetchAuthSession } from "aws-amplify/auth";
import NewNavbar from "../NewAssetDashboard/NewNavbar";
import NewDashboardHeader from "./NewDashboardHeader";
import GroupWidget from "./GroupWidget";
import GroupWidgetFooter from "./GroupWidgetFooter";
import { modalViewsConst, setModalState } from "../../store/modalSlice";
import { NoAssetGroup } from "../../components/AssetGroupFolder";
import LoadingIcon from "../../components/LoadingIcon";
import { fetchAssetGroups } from "../../store/assetGroupSlice";
// This component represents the main dashboard of the application.
const NewDashboard = () => {
  const LoadingIndicator = LoadingIcon as React.ComponentType<{ fullPage?: boolean }>;
  const dispatch = useDispatch<AppDispatch>();
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const user = useSelector((state: RootState) => state.user) as any;
  const assetGroups = useSelector((state: RootState) => state.assetGroups);
  const assets = useSelector((state: RootState) => state.assets);

  const hasAssetGroups = Object.keys(assetGroups?.groups || {}).length > 0;

  const allLoadedAssets = useMemo(() => {
    return Object.values(assets?.dataGroups || {}).flat() as any[];
  }, [assets?.dataGroups]);

  const totalAssets = allLoadedAssets.length;

  const totalModels = useMemo(() => { // calculates the total number of processed models across all loaded assets.
    return allLoadedAssets.reduce((count, asset: any) => {
      const modelCount = asset?.processedModels?.items?.length || 0;
      return count + modelCount;
    }, 0);
  }, [allLoadedAssets]);

  const handleAddAssetGroup = async () => { // this handles the logic for adding a new asset group.
    const authSession = await fetchAuthSession();
    const rawGroups = authSession?.tokens?.idToken?.payload?.["cognito:groups"];
    const groups = Array.isArray(rawGroups) ? rawGroups : [];
    const assetGroupCountCap = user?.permissionDetails?.assetGroupCount ?? 1;
    const currentGroupCount = Object.keys(assetGroups?.groups || {}).length;

    if (groups.includes("Admin") || currentGroupCount < assetGroupCountCap) { // only allow users in the Admin group or users who have not yet reached the asset group count cap to add new asset groups.
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.addAssetGroup,
          properties: null,
        })
      );
      return;
    }

    dispatch( // if the user is not allowed to add more asset groups, show a notification modal with a quick note.
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.notification,
        properties: {
          __modalSize: "sm",
          header: "Quick Note",
          details: `Asset Groups are currently being capped at ${assetGroupCountCap} per user. Please reach out to support@erode.ai for any inquiries`,
        },
      })
    );
  };

  useEffect(() => { // this checks if there are already asset groups in the state, and if not, it fetches them from the backend.
    let mounted = true;

    const bootstrapGroups = async () => { // this function bootstraps the asset groups by checking if they are already in the state and fetching them if not.
      const hasGroups = Object.keys(assetGroups?.groups || {}).length > 0;
      if (!hasGroups) {
        await dispatch(fetchAssetGroups());
      }
      if (mounted) {
        setIsBootstrapping(false);
      }
    };

    bootstrapGroups();
    return () => {
      mounted = false;
    };
  }, [dispatch]);

  if (isBootstrapping || assetGroups?.loading === "pending") {
        return (
      <div className="flex-1 flex h-screen max-h-screen max-w-full overflow-hidden">
        <div className="rounded-[8px] bg-white p-4 flex-1 flex items-center justify-center max-w-full min-h-0">
          <LoadingIndicator fullPage={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex h-screen max-h-screen max-w-full overflow-hidden">
      <div className="rounded-[8px] p-3 sm:p-4 flex-1 flex flex-col max-w-full min-h-0 gap-3">
        <NewNavbar />
        <NewDashboardHeader
          onAddGroup={handleAddAssetGroup}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
        />
        <div className="flex-1 min-h-0 overflow-y-auto scrollable-div gap-3 flex flex-col">
          {hasAssetGroups ? (
            <div className="flex flex-col gap-3">
              <GroupWidget searchQuery={searchValue} />
              <GroupWidgetFooter totalAssets={totalAssets} totalModels={totalModels} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <NoAssetGroup handleAddAssetGroup={handleAddAssetGroup} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewDashboard;
